<script setup lang="ts">
import { useMagicKeys, useToggle } from '@vueuse/core'
import { provide, ref, toRefs, watch } from 'vue'
import { type AccordeonContext, AccordionContextSymbol } from './context'

import {
  useCustomEventListener,
  useDispatchCustomEvent,
} from '@/composables/useCustomEventListener'

export interface Props {
  is?: string
  isOpen?: boolean
  isDisabled?: boolean
  groupId?: string
  accordionId?: string
  ignoreEvents?: boolean
  hiddenHeader?: boolean
}

export interface AccordeonEmits {
  (event: 'onToggle', value?: boolean): void
  (event: 'onOpen'): void
  (event: 'onClose'): void
}

const {
  isOpen = false,
  ...props
} = defineProps<Props>()

const emit = defineEmits<AccordeonEmits>()

const { ignoreEvents, isDisabled } = toRefs(props)

const accID = crypto.randomUUID()
const id = props.accordionId ? props.accordionId : `accordeon-${accID}`

const wrapperRef = ref<HTMLDivElement>()

const [open, toggle] = useToggle(isOpen ?? false)

const handlerToggle = (value?: boolean) => {
  if (isDisabled.value)
    return
  if (value !== undefined)
    toggle(value)
  else toggle()
}

const { escape } = useMagicKeys({})

watch(escape, (value) => {
  if (value && open.value)
    toggle(false)
})

watch(() => isOpen, () => {
  toggle(isOpen)
})

watch(open, (value) => {
  if (ignoreEvents.value)
    return
  emit('onToggle', value)
  if (value)
    emit('onOpen')
  else
    emit('onClose')
  useDispatchCustomEvent('accordeon-toggle', {
    id,
    value,
    groupId: props.groupId,
  })
})
useCustomEventListener('accordeon-toggle', (event) => {
  if (ignoreEvents.value || !props.groupId)
    return
  if (event.id === id)
    return
  if (event.groupId === props.groupId && open.value) {
    if (event.id !== id && event.value)
      toggle(false)
  }
})

defineExpose<AccordeonContext>({
  isOpen: open,
  toggle,
  wrapperRef,
})

provide(AccordionContextSymbol, {
  isOpen: open,
  toggle: handlerToggle,
  wrapperRef,
})
</script>

<template>
  <component
    :is="props.is ?? 'div'"
    :id="`accordeon-${accID}`"
    ref="wrapperRef"
    :aria-expanded="open"
    role="listbox"
    tabindex="1"
    :class="[$attrs.class]"
  >
    <div
      v-if="!props.hiddenHeader"
      role="button" :aria-disabled="isDisabled" :class="`accordeon-trigger-${accID} overflow-hidden w-full `" :aria-pressed="isOpen"
    >
      <slot
        name="header"
        :toggle="handlerToggle"
        :is-open="open"
        :is-disabled="isDisabled"
      />
    </div>
    <Collapsable :aria-hidden="!open" :aria-expanded="open">
      <div class="accordeon-content" :class="$style['no-scrollbar']">
        <slot
          name="content"
          :toggle="handlerToggle"
          :is-open="open"
          :is-disabled="isDisabled"
        />
      </div>
    </Collapsable>
  </component>
</template>

<style lang="scss" module>
.no-scrollbar{
  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
